import { consola } from 'consola'
import { openTBusinessModal, openYooKassaModal, openAlfaBankModal, showGoingToPaymentAlert, showErrorAlert } from './modalUtils'
import type { Order } from '@entities/order'

/**
 * Типы поддерживаемых платежных систем
 */
export type PaymentSystemType = 'tbusiness' | 'yookassa' | 'alfabank' | 'sberbank' | 'unknown'

/**
 * Константы платежных систем
 */
export const PAYMENT_SYSTEMS = {
  TBUSINESS: 'tbusiness' as const,
  YOOKASSA: 'yookassa' as const,
  ALFABANK: 'alfabank' as const,
  SBERBANK: 'sberbank' as const,
} as const

/**
 * Константы валют (ISO 4217)
 */
export const CURRENCY_CODES = {
  RUB: 643, // Российский рубль
  USD: 840, // Доллар США  
  EUR: 978,  // Евро
} as const

/**
 * Константы для АльфаБанка
 */
export const ALFABANK_CONSTANTS = {
  DEFAULT_CURRENCY: CURRENCY_CODES.RUB,
  DEFAULT_LANGUAGE: 'ru' as const,
  SUPPORTED_LANGUAGES: ['ru', 'en'] as const,
  ERROR_CODES: {
    UNKNOWN_CURRENCY: 3,
    INVALID_MERCHANT: 5,
    INVALID_AMOUNT: 6,
    ORDER_NOT_FOUND: 2,
  } as const,
} as const

/**
 * Определяет тип платежной системы заказа
 * @param order - заказ для проверки
 * @returns тип платежной системы
 */
export function getPaymentSystemType(order: Order | null): PaymentSystemType {
  if (!order || !order.active_payment_system) {
    return 'unknown'
  }
  
  const system = order.active_payment_system.toLowerCase()
  
  if (system === PAYMENT_SYSTEMS.TBUSINESS) {
    return PAYMENT_SYSTEMS.TBUSINESS
  }
  
  if (system === PAYMENT_SYSTEMS.YOOKASSA) {
    return PAYMENT_SYSTEMS.YOOKASSA
  }

  if (system === PAYMENT_SYSTEMS.ALFABANK) {
    return PAYMENT_SYSTEMS.ALFABANK
  }

  if (system === PAYMENT_SYSTEMS.SBERBANK) {
    return PAYMENT_SYSTEMS.SBERBANK
  }
  
  return 'unknown'
}

/**
 * Проверяет, поддерживается ли платежная система
 * @param paymentSystem - тип платежной системы
 * @returns true если система поддерживается
 */
export function isSupportedPaymentSystem(paymentSystem: PaymentSystemType): boolean {
  return paymentSystem === PAYMENT_SYSTEMS.TBUSINESS || 
         paymentSystem === PAYMENT_SYSTEMS.YOOKASSA || 
         paymentSystem === PAYMENT_SYSTEMS.ALFABANK ||
         paymentSystem === PAYMENT_SYSTEMS.SBERBANK
}

/**
 * Проверяет наличие URL для оплаты в заказе
 * @param order - заказ для проверки
 * @returns true если URL есть
 */
export function hasPaymentUrl(order: Order | null): boolean {
  return !!(order && order.payment_url)
}

/**
 * Открывает форму оплаты через TBusiness
 * @param order - заказ для оплаты
 */
export function openTBusinessPayment(order: Order): void {
  consola.debug('Opening TBusiness payment for order:', order.id)
  openTBusinessModal()
}

/**
 * Открывает форму оплаты через YooKassa
 * @param order - заказ для оплаты
 */
export function openYooKassaPayment(order: Order): void {
  consola.debug('Opening YooKassa payment for order:', order.id)
  openYooKassaModal()
}

/**
 * Открывает форму оплаты через Альфа-Банк
 * @param order - заказ для оплаты
 */
export function openAlfaBankPayment(order: Order): void {
  consola.debug('Opening Alfa Bank payment for order:', order.id)
  openAlfaBankModal(order.payment_url)
}

/**
 * Открывает форму оплаты через Sberbank
 * @param order - заказ для оплаты
 */
export function openSberbankPayment(order: Order): void {
  consola.debug('Opening Sberbank payment for order:', order.id)
  if (order.payment_url) {
    openDirectPaymentUrl(order.payment_url)
  } else {
    consola.error('No payment URL for Sberbank payment')
    showErrorAlert('Нет ссылки для оплаты через Сбербанк')
  }
}

/**
 * Открывает прямую ссылку для оплаты в новом окне
 * @param paymentUrl - URL для оплаты
 */
export function openDirectPaymentUrl(paymentUrl: string): void {
  consola.debug('Opening direct payment URL:', paymentUrl)
  window.open(paymentUrl, '_blank')
  showGoingToPaymentAlert()
}

/**
 * Инициирует оплату на основе платежной системы заказа
 * @param order - заказ для оплаты
 * @returns true если оплата была инициирована успешно
 */
export function initiatePaymentBySystem(order: Order): boolean {
  const paymentSystem = getPaymentSystemType(order)
  
  consola.debug('Initiating payment for system:', paymentSystem)
  
  switch (paymentSystem) {
    case PAYMENT_SYSTEMS.TBUSINESS: {
      openTBusinessPayment(order)
      return true
    }
      
    case PAYMENT_SYSTEMS.YOOKASSA: {
      openYooKassaPayment(order)
      return true
    }
      
    case PAYMENT_SYSTEMS.ALFABANK: {
      openAlfaBankPayment(order)
      return true
    }

    case PAYMENT_SYSTEMS.SBERBANK: {
      openSberbankPayment(order)
      return true
    }
      
    default: {
      // Fallback: если система не определена, используем прямую ссылку
      consola.debug('Unknown payment system, trying direct URL')
      if (hasPaymentUrl(order)) {
        openDirectPaymentUrl(order.payment_url!)
        return true
      }
      
      consola.error('No payment method available for order:', order.id)
      showErrorAlert('Нет доступного способа оплаты для этого заказа')
      return false
    }
  }
}

/**
 * Проверяет, можно ли инициировать оплату для заказа
 * @param order - заказ для проверки
 * @returns true если оплату можно инициировать
 */
export function canInitiatePayment(order: Order | null): boolean {
  if (!order) return false
  
  const paymentSystem = getPaymentSystemType(order)
  
  // Если система поддерживается, можно инициировать
  if (isSupportedPaymentSystem(paymentSystem)) {
    return true
  }
  
  // Если система неизвестна, проверяем наличие прямой ссылки
  return hasPaymentUrl(order)
}

/**
 * Получает название платежной системы для отображения
 * @param paymentSystem - тип платежной системы
 * @returns название для отображения
 */
export function getPaymentSystemDisplayName(paymentSystem: PaymentSystemType): string {
  switch (paymentSystem) {
    case PAYMENT_SYSTEMS.TBUSINESS: {
      return 'TBusiness'
    }
    case PAYMENT_SYSTEMS.YOOKASSA: {
      return 'YooKassa'
    }
    case PAYMENT_SYSTEMS.ALFABANK: {
      return 'Альфа-Банк'
    }
    case PAYMENT_SYSTEMS.SBERBANK: {
      return 'Сбербанк'
    }
    default: {
      return 'Внешняя платежная система'
    }
  }
}

/**
 * Логирует информацию о платежной системе
 * @param order - заказ для логирования
 */
export function logPaymentSystemInfo(order: Order | null): void {
  if (!order) {
    consola.debug('💳 No order for payment system info')
    return
  }
  
  const paymentSystem = getPaymentSystemType(order)
  const displayName = getPaymentSystemDisplayName(paymentSystem)
  const hasUrl = hasPaymentUrl(order)
  
  consola.debug('💳 Payment system info:', {
    order_id: order.id,
    system: paymentSystem,
    display_name: displayName,
    has_url: hasUrl,
    payment_url: order.payment_url,
    can_initiate: canInitiatePayment(order),
  })
}

/**
 * Валидирует данные заказа для оплаты
 * @param order - заказ для валидации
 * @returns true если данные валидны для оплаты
 */
export function validateOrderForPayment(order: Order | null): boolean {
  if (!order || !order.id) {
    consola.warn('Invalid order: missing order or order ID')
    return false
  }
  
  if (!canInitiatePayment(order)) {
    consola.warn('Cannot initiate payment for order:', order.id)
    return false
  }
  
  return true
}